/**
 * The Wordle Solver: which words still fit the clues so far.
 *
 * The solver never reasons about "this letter is yellow, so it is somewhere else". It
 * does the one thing the game itself does: score a guess against an answer. A candidate
 * word fits the clues exactly when scoring each guess against it reproduces the colors
 * the player saw. That makes repeated letters correct by construction — a second E in a
 * guess comes back gray when the answer has only one E, and the solver agrees because it
 * runs the same scoring rule rather than a simplified one.
 */

/** 0 = gray (not in the word, or no more of that letter), 1 = yellow, 2 = green. */
export type Mark = 0 | 1 | 2;

export const ABSENT: Mark = 0;
export const PRESENT: Mark = 1;
export const CORRECT: Mark = 2;

export const MIN_LENGTH = 4;
export const MAX_LENGTH = 8;
export const DEFAULT_LENGTH = 5;

const LETTER_A = 97;
const ALPHABET = 26;
const RE_LETTERS = /^[a-z]+$/;

export interface Clue {
  guess: string;
  marks: readonly Mark[];
}

/** Lowercase letters only, or null when the text is not a clean guess. */
export function cleanGuess(text: string): string | null {
  const word = text.trim().toLowerCase();
  return RE_LETTERS.test(word) ? word : null;
}

/**
 * Score a guess against an answer the way the game does: greens first, then each
 * remaining guess letter is yellow only while the answer still has an unmatched copy of
 * it, working left to right.
 */
export function scoreGuess(guess: string, answer: string): Mark[] {
  const marks: Mark[] = new Array(guess.length).fill(ABSENT);
  const spare = new Uint8Array(ALPHABET);
  for (let i = 0; i < guess.length; i += 1) {
    if (guess[i] === answer[i]) {
      marks[i] = CORRECT;
    } else {
      spare[answer.charCodeAt(i) - LETTER_A] += 1;
    }
  }
  for (let i = 0; i < guess.length; i += 1) {
    if (marks[i] === CORRECT) {
      continue;
    }
    const slot = guess.charCodeAt(i) - LETTER_A;
    if (spare[slot] > 0) {
      spare[slot] -= 1;
      marks[i] = PRESENT;
    }
  }
  return marks;
}

/** A pattern as one number (base 3), for bucketing words by the colors they give. */
export function patternCode(marks: readonly Mark[]): number {
  let code = 0;
  for (const mark of marks) {
    code = code * 3 + mark;
  }
  return code;
}

/** Marks as letters for tests and copy: b = gray, y = yellow, g = green. */
export function marksToText(marks: readonly Mark[]): string {
  return marks.map((mark) => ["b", "y", "g"][mark]).join("");
}

/** Does `word` reproduce every clue's colors? */
export function fitsClues(word: string, clues: readonly Clue[]): boolean {
  for (const clue of clues) {
    const marks = scoreGuess(clue.guess, word);
    for (let i = 0; i < marks.length; i += 1) {
      if (marks[i] !== clue.marks[i]) {
        return false;
      }
    }
  }
  return true;
}

/** The words of one length that fit all the clues, in list order. */
export function filterCandidates(
  words: readonly string[],
  length: number,
  clues: readonly Clue[]
): string[] {
  const found: string[] = [];
  for (const word of words) {
    if (word.length === length && fitsClues(word, clues)) {
      found.push(word);
    }
  }
  return found;
}

export interface Ranked {
  word: string;
  score: number;
}

/**
 * Rank by letter coverage: a letter's weight is how many remaining candidates contain
 * it, and a word scores the sum over its distinct letters (a repeated letter earns
 * nothing the second time, because it reveals nothing new). Ties break alphabetically.
 */
export function rankByLetterFrequency(candidates: readonly string[]): Ranked[] {
  const weight = new Uint32Array(ALPHABET);
  for (const word of candidates) {
    for (const slot of distinctSlots(word)) {
      weight[slot] += 1;
    }
  }
  return candidates
    .map((word) => ({
      word,
      score: distinctSlots(word).reduce((sum, slot) => sum + weight[slot], 0),
    }))
    .sort((a, b) => b.score - a.score || a.word.localeCompare(b.word));
}

function distinctSlots(word: string): number[] {
  const seen = new Set<number>();
  for (let i = 0; i < word.length; i += 1) {
    seen.add(word.charCodeAt(i) - LETTER_A);
  }
  return [...seen];
}

export interface Split {
  word: string;
  /** Average number of candidates left after this guess, if each is equally likely. */
  expected: number;
  /** The biggest group of candidates this guess leaves together. */
  worst: number;
}

/** Above this many candidates the split ranking is skipped (it is N × N scorings). */
export const MAX_SPLIT_CANDIDATES = 500;

/**
 * Rank each candidate as a guess by how finely it splits the remaining candidates:
 * for every possible answer, score the guess and group the answers by the colors that
 * would appear. The expected number left is Σ(group size²) ÷ N — smaller is better.
 */
export function rankBySplit(candidates: readonly string[]): Split[] {
  const total = candidates.length;
  const ranked: Split[] = [];
  for (const guess of candidates) {
    const groups = new Map<number, number>();
    for (const answer of candidates) {
      const code = patternCode(scoreGuess(guess, answer));
      groups.set(code, (groups.get(code) ?? 0) + 1);
    }
    let squares = 0;
    let worst = 0;
    for (const size of groups.values()) {
      squares += size * size;
      worst = Math.max(worst, size);
    }
    ranked.push({ word: guess, expected: squares / total, worst });
  }
  return ranked.sort(
    (a, b) =>
      a.expected - b.expected ||
      a.worst - b.worst ||
      a.word.localeCompare(b.word)
  );
}

export interface Knowledge {
  /** The letter fixed at each position by a green, or "" while unknown. */
  pattern: string[];
  /** Letters known to be in the word, with the fewest copies the clues prove. */
  contains: { letter: string; atLeast: number }[];
  /** Letters the clues rule out completely. */
  excluded: string[];
}

/** What the clues prove, for a summary line. Derived from the marks, not the answer. */
export function summarize(length: number, clues: readonly Clue[]): Knowledge {
  const pattern: string[] = new Array(length).fill("");
  const atLeast = new Map<string, number>();
  const everInWord = new Set<string>();
  const seen = new Set<string>();
  for (const clue of clues) {
    const counts = new Map<string, number>();
    for (let i = 0; i < clue.guess.length; i += 1) {
      const letter = clue.guess[i];
      seen.add(letter);
      if (clue.marks[i] === CORRECT) {
        pattern[i] = letter;
      }
      if (clue.marks[i] !== ABSENT) {
        counts.set(letter, (counts.get(letter) ?? 0) + 1);
        everInWord.add(letter);
      }
    }
    for (const [letter, count] of counts) {
      atLeast.set(letter, Math.max(atLeast.get(letter) ?? 0, count));
    }
  }
  return {
    pattern,
    contains: [...atLeast]
      .map(([letter, n]) => ({ letter, atLeast: n }))
      .sort((a, b) => a.letter.localeCompare(b.letter)),
    excluded: [...seen].filter((letter) => !everInWord.has(letter)).sort(),
  };
}
